import { CommandHandlerMap } from '@commandDrivenStateEngine/core/types';
import { emptyBillingEntityFormData } from '@domains/account/state/state';
import { SalesChannelSetupState } from './state';
import { SalesChannelSetupCommand } from './commands';

/**
 * Pure handlers for the sales-channel setup wizard (TV1-1904, rule-based rework).
 *
 * Rules: pure, immutable, never dispatch other commands. The submit/start and
 * create/start handlers only flip a loading flag; the submit policy performs the
 * backend write (SetRule / UpdateSystemIntegrationConfiguration / Provision /
 * AddBillingEntity) and dispatches the success/error outcome.
 */

type Cmd<T extends SalesChannelSetupCommand['type']> = Extract<
  SalesChannelSetupCommand,
  { type: T }
>;

const handleDataSet = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/data/set'>
): SalesChannelSetupState => ({
  ...state,
  data: {
    integrationId: cmd.payload.integrationId,
    accountId: cmd.payload.accountId,
    connectorId: cmd.payload.connectorId,
    integrationUserId: cmd.payload.integrationUserId,
    channelSetupCompletedAt: cmd.payload.channelSetupCompletedAt,
    currentSettings: cmd.payload.currentSettings,
    currentBillingEntityId: cmd.payload.currentBillingEntityId
  },
  // Inventory/logistics are pre-filled from rules (rules/sync), not channel
  // settings. Only the billing entity is synced from the channel here.
  billing: {
    ...state.billing,
    billingEntityId: cmd.payload.currentBillingEntityId
  }
});

const handleRulesSync = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/rules/sync'>
): SalesChannelSetupState => ({
  ...state,
  inventory: cmd.payload.warehouse
    ? {
        warehouseId: cmd.payload.warehouse.warehouseId,
        trigger: cmd.payload.warehouse.trigger,
        statusId: cmd.payload.warehouse.statusId,
        ruleId: cmd.payload.warehouse.ruleId,
        channelStockRuleId: cmd.payload.warehouse.channelStockRuleId
      }
    : state.inventory,
  logistics: cmd.payload.logistics
    ? {
        ...state.logistics,
        logisticsIntegrationId: cmd.payload.logistics.logisticsIntegrationId,
        trigger: cmd.payload.logistics.trigger,
        statusId: cmd.payload.logistics.statusId,
        ruleId: cmd.payload.logistics.ruleId
      }
    : state.logistics,
  billing: cmd.payload.billing
    ? {
        ...state.billing,
        trigger: cmd.payload.billing.trigger,
        statusId: cmd.payload.billing.statusId,
        ruleId: cmd.payload.billing.ruleId
      }
    : state.billing
});

// =============================================================================
// UI
// =============================================================================

const handleWizardOpen = (
  state: SalesChannelSetupState
): SalesChannelSetupState => ({
  ...state,
  ui: { ...state.ui, manuallyOpened: true }
});

const handleWizardClose = (
  state: SalesChannelSetupState
): SalesChannelSetupState => ({
  ...state,
  ui: { ...state.ui, manuallyOpened: false }
});

// =============================================================================
// CATALOGS
// =============================================================================

const handleCatalogsSetWarehouses = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/catalogs/setWarehouses'>
): SalesChannelSetupState => ({
  ...state,
  catalogs: { ...state.catalogs, availableWarehouses: cmd.payload.warehouses }
});

const handleCatalogsSetBillingEntities = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/catalogs/setBillingEntities'>
): SalesChannelSetupState => ({
  ...state,
  catalogs: { ...state.catalogs, billingEntities: cmd.payload.billingEntities }
});

const handleCatalogsSetLogistics = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/catalogs/setLogistics'>
): SalesChannelSetupState => ({
  ...state,
  catalogs: { ...state.catalogs, logisticsIntegrations: cmd.payload.logisticsIntegrations }
});

const handleCatalogsSetInvoicingIntegrations = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/catalogs/setInvoicingIntegrations'>
): SalesChannelSetupState => ({
  ...state,
  catalogs: { ...state.catalogs, invoicingIntegrations: cmd.payload.invoicingIntegrations }
});

const handleCatalogsSetOrderStatuses = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/catalogs/setOrderStatuses'>
): SalesChannelSetupState => ({
  ...state,
  catalogs: { ...state.catalogs, orderStatuses: cmd.payload.orderStatuses }
});

// =============================================================================
// INVENTORY
// =============================================================================

const handlePublicationsSetAutoImport = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/publications/setAutoImport'>
): SalesChannelSetupState => ({
  ...state,
  publications: { ...state.publications, autoImport: cmd.payload.autoImport }
});

/**
 * TV1-2379 — independent of `autoImport` above: it writes only its own field, so
 * arming the order history never disturbs the publications choice and vice versa.
 */
const handlePublicationsSetAutoImportOrders = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/publications/setAutoImportOrders'>
): SalesChannelSetupState => ({
  ...state,
  publications: { ...state.publications, autoImportOrders: cmd.payload.autoImportOrders }
});

const handleInventorySetWarehouse = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/inventory/setWarehouse'>
): SalesChannelSetupState => ({
  ...state,
  inventory: { ...state.inventory, warehouseId: cmd.payload.warehouseId }
});

const handleInventorySetTrigger = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/inventory/setTrigger'>
): SalesChannelSetupState => ({
  ...state,
  inventory: {
    ...state.inventory,
    trigger: cmd.payload.trigger,
    // A placement trigger has no status filter.
    statusId: cmd.payload.trigger === 'OrderPlaced' ? null : state.inventory.statusId
  }
});

const handleInventorySetStatus = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/inventory/setStatus'>
): SalesChannelSetupState => ({
  ...state,
  inventory: { ...state.inventory, statusId: cmd.payload.statusId }
});

const handleInventorySubmitStart = (
  state: SalesChannelSetupState
): SalesChannelSetupState => ({
  ...state,
  loading: { ...state.loading, savingInventory: true },
  errors: {}
});

const handleInventorySubmitSuccess = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/inventory/submit/success'>
): SalesChannelSetupState => ({
  ...state,
  inventory: {
    ...state.inventory,
    ruleId: cmd.payload.ruleId ?? state.inventory.ruleId,
    channelStockRuleId: cmd.payload.channelStockRuleId ?? state.inventory.channelStockRuleId
  },
  loading: { ...state.loading, savingInventory: false }
});

const handleInventorySubmitError = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/inventory/submit/error'>
): SalesChannelSetupState => ({
  ...state,
  loading: { ...state.loading, savingInventory: false },
  errors: { ...state.errors, inventory: cmd.payload.error ?? 'No se pudo guardar la regla de inventario' }
});

// =============================================================================
// BILLING
// =============================================================================

const handleBillingSelectEntity = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/billing/selectEntity'>
): SalesChannelSetupState => ({
  ...state,
  billing: { ...state.billing, billingEntityId: cmd.payload.billingEntityId }
});

const handleBillingSetMode = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/billing/setMode'>
): SalesChannelSetupState => ({
  ...state,
  billing: { ...state.billing, mode: cmd.payload.mode, formErrors: {}}
});

const handleBillingUpdateField = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/billing/updateField'>
): SalesChannelSetupState => {
  const formErrors = { ...state.billing.formErrors };
  delete formErrors[cmd.payload.field];
  return {
    ...state,
    billing: {
      ...state.billing,
      form: { ...state.billing.form, [cmd.payload.field]: cmd.payload.value },
      formErrors
    }
  };
};

const handleBillingSetFormErrors = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/billing/setFormErrors'>
): SalesChannelSetupState => ({
  ...state,
  billing: { ...state.billing, formErrors: cmd.payload.errors }
});

const handleBillingSetTrigger = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/billing/setTrigger'>
): SalesChannelSetupState => ({
  ...state,
  billing: { ...state.billing, trigger: cmd.payload.trigger }
});

const handleBillingSetStatus = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/billing/setStatus'>
): SalesChannelSetupState => ({
  ...state,
  billing: { ...state.billing, statusId: cmd.payload.statusId }
});

const handleBillingSubmitStart = (
  state: SalesChannelSetupState
): SalesChannelSetupState => ({
  ...state,
  loading: { ...state.loading, savingBilling: true },
  errors: {}
});

const handleBillingSubmitSuccess = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/billing/submit/success'>
): SalesChannelSetupState => ({
  ...state,
  data: { ...state.data, currentBillingEntityId: state.billing.billingEntityId },
  billing: { ...state.billing, ruleId: cmd.payload.ruleId ?? state.billing.ruleId },
  loading: { ...state.loading, savingBilling: false }
});

const handleBillingSubmitError = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/billing/submit/error'>
): SalesChannelSetupState => ({
  ...state,
  loading: { ...state.loading, savingBilling: false },
  errors: {
    ...state.errors,
    billing: cmd.payload.error ?? 'No se pudo guardar la entidad de facturación'
  }
});

const handleBillingCreateStart = (
  state: SalesChannelSetupState
): SalesChannelSetupState => ({
  ...state,
  loading: { ...state.loading, creatingBilling: true },
  errors: {}
});

const handleBillingCreateSuccess = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/billing/create/success'>
): SalesChannelSetupState => {
  // Optimistically add the new entity to the catalog. The catalog is query-loaded
  // once at init and never refreshed, and the readmodel lags the command, so
  // without this the just-created entity is absent from the select-mode dropdown
  // (rendering it blank / appearing to revert to create mode).
  const alreadyListed = state.catalogs.billingEntities.some(
    ( e ) => e.id === cmd.payload.billingEntityId
  );
  const billingEntities = alreadyListed
    ? state.catalogs.billingEntities
    : [ ...state.catalogs.billingEntities, { id: cmd.payload.billingEntityId, name: cmd.payload.name }];

  return {
    ...state,
    catalogs: { ...state.catalogs, billingEntities },
    billing: {
      ...state.billing,
      mode: 'select',
      billingEntityId: cmd.payload.billingEntityId,
      form: emptyBillingEntityFormData,
      formErrors: {}
    },
    loading: { ...state.loading, creatingBilling: false }
  };
};

const handleBillingCreateError = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/billing/create/error'>
): SalesChannelSetupState => ({
  ...state,
  loading: { ...state.loading, creatingBilling: false },
  errors: {
    ...state.errors,
    billing: cmd.payload.error ?? 'No se pudo crear la entidad de facturación'
  }
});

// =============================================================================
// INVOICING (inline-creation prerequisite)
// =============================================================================

const handleInvoicingSelectConnector = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/invoicing/selectConnector'>
): SalesChannelSetupState => ({
  ...state,
  invoicing: { ...state.invoicing, selectedConnectorId: cmd.payload.connectorId, credentials: {}}
});

const handleInvoicingUpdateCredentials = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/invoicing/updateCredentials'>
): SalesChannelSetupState => ({
  ...state,
  invoicing: { ...state.invoicing, credentials: cmd.payload.credentials }
});

const handleInvoicingProvisionStart = (
  state: SalesChannelSetupState
): SalesChannelSetupState => ({
  ...state,
  loading: { ...state.loading, provisioningInvoicing: true },
  errors: {}
});

const handleInvoicingProvisionSuccess = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/invoicing/provision/success'>
): SalesChannelSetupState => ({
  ...state,
  invoicing: { ...state.invoicing, provisionedIntegrationId: cmd.payload.integrationId },
  billing: {
    ...state.billing,
    form: { ...state.billing.form, invoicingIntegrationId: cmd.payload.integrationId }
  },
  loading: { ...state.loading, provisioningInvoicing: false }
});

const handleInvoicingProvisionError = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/invoicing/provision/error'>
): SalesChannelSetupState => ({
  ...state,
  loading: { ...state.loading, provisioningInvoicing: false },
  errors: {
    ...state.errors,
    invoicing: cmd.payload.error ?? 'No se pudo conectar la integración de facturación'
  }
});

// =============================================================================
// LOGISTICS
// =============================================================================

const handleLogisticsSelectIntegration = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/logistics/selectIntegration'>
): SalesChannelSetupState => ({
  ...state,
  logistics: { ...state.logistics, logisticsIntegrationId: cmd.payload.logisticsIntegrationId }
});

const handleLogisticsSetTrigger = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/logistics/setTrigger'>
): SalesChannelSetupState => ({
  ...state,
  logistics: {
    ...state.logistics,
    trigger: cmd.payload.trigger,
    statusId: cmd.payload.trigger === 'OrderPlaced' ? null : state.logistics.statusId
  }
});

const handleLogisticsSetStatus = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/logistics/setStatus'>
): SalesChannelSetupState => ({
  ...state,
  logistics: { ...state.logistics, statusId: cmd.payload.statusId }
});

const handleLogisticsSubmitStart = (
  state: SalesChannelSetupState
): SalesChannelSetupState => ({
  ...state,
  loading: { ...state.loading, savingLogistics: true },
  errors: {}
});

const handleLogisticsSubmitSuccess = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/logistics/submit/success'>
): SalesChannelSetupState => ({
  ...state,
  logistics: { ...state.logistics, ruleId: cmd.payload.ruleId ?? state.logistics.ruleId },
  loading: { ...state.loading, savingLogistics: false }
});

const handleLogisticsSubmitError = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/logistics/submit/error'>
): SalesChannelSetupState => ({
  ...state,
  loading: { ...state.loading, savingLogistics: false },
  errors: { ...state.errors, logistics: cmd.payload.error ?? 'No se pudo guardar la regla de logística' }
});

const handleLogisticsSetMode = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/logistics/setMode'>
): SalesChannelSetupState => ({
  ...state,
  logistics: { ...state.logistics, mode: cmd.payload.mode }
});

const handleLogisticsCreateSelectConnector = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/logistics/create/selectConnector'>
): SalesChannelSetupState => ({
  ...state,
  logistics: {
    ...state.logistics,
    create: {
      selectedConnectorId: cmd.payload.connectorId,
      credentials: {},
      instanceName: ''
    }
  }
});

const handleLogisticsCreateUpdateCredentials = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/logistics/create/updateCredentials'>
): SalesChannelSetupState => ({
  ...state,
  logistics: {
    ...state.logistics,
    create: { ...state.logistics.create, credentials: cmd.payload.credentials }
  }
});

const handleLogisticsCreateSetInstanceName = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/logistics/create/setInstanceName'>
): SalesChannelSetupState => ({
  ...state,
  logistics: {
    ...state.logistics,
    create: { ...state.logistics.create, instanceName: cmd.payload.instanceName }
  }
});

const handleLogisticsRegisterStart = (
  state: SalesChannelSetupState
): SalesChannelSetupState => ({
  ...state,
  loading: { ...state.loading, registeringLogistics: true },
  errors: {}
});

const handleLogisticsRegisterSuccess = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/logistics/register/success'>
): SalesChannelSetupState => ({
  ...state,
  logistics: {
    ...state.logistics,
    mode: 'select',
    logisticsIntegrationId: cmd.payload.integrationId ?? state.logistics.logisticsIntegrationId,
    create: { selectedConnectorId: null, credentials: {}, instanceName: '' }
  },
  loading: { ...state.loading, registeringLogistics: false }
});

const handleLogisticsRegisterError = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/logistics/register/error'>
): SalesChannelSetupState => ({
  ...state,
  loading: { ...state.loading, registeringLogistics: false },
  errors: {
    ...state.errors,
    logistics: cmd.payload.error ?? 'No se pudo registrar la integración de logística'
  }
});

// =============================================================================
// COMPLETE
// =============================================================================

const handleCompleteStart = (
  state: SalesChannelSetupState
): SalesChannelSetupState => ({
  ...state,
  loading: { ...state.loading, completing: true },
  errors: {}
});

const handleCompleteSuccess = (
  state: SalesChannelSetupState
): SalesChannelSetupState => ({
  ...state,
  data: {
    ...state.data,
    channelSetupCompletedAt:
      state.data.channelSetupCompletedAt ?? new Date().toISOString()
  },
  ui: { ...state.ui, manuallyOpened: false },
  loading: { ...state.loading, completing: false }
});

const handleCompleteError = (
  state: SalesChannelSetupState,
  cmd: Cmd<'salesChannelSetup/complete/error'>
): SalesChannelSetupState => ({
  ...state,
  loading: { ...state.loading, completing: false },
  errors: { ...state.errors, complete: cmd.payload.error ?? 'No se pudo completar la configuración' }
});

export const handlers: CommandHandlerMap<SalesChannelSetupState, SalesChannelSetupCommand> = {
  'salesChannelSetup/data/set': handleDataSet,
  'salesChannelSetup/rules/sync': handleRulesSync,
  'salesChannelSetup/wizard/open': handleWizardOpen,
  'salesChannelSetup/wizard/close': handleWizardClose,
  'salesChannelSetup/catalogs/setWarehouses': handleCatalogsSetWarehouses,
  'salesChannelSetup/catalogs/setBillingEntities': handleCatalogsSetBillingEntities,
  'salesChannelSetup/catalogs/setLogistics': handleCatalogsSetLogistics,
  'salesChannelSetup/catalogs/setInvoicingIntegrations': handleCatalogsSetInvoicingIntegrations,
  'salesChannelSetup/catalogs/setOrderStatuses': handleCatalogsSetOrderStatuses,
  'salesChannelSetup/publications/setAutoImport': handlePublicationsSetAutoImport,
  'salesChannelSetup/publications/setAutoImportOrders': handlePublicationsSetAutoImportOrders,
  'salesChannelSetup/inventory/setWarehouse': handleInventorySetWarehouse,
  'salesChannelSetup/inventory/setTrigger': handleInventorySetTrigger,
  'salesChannelSetup/inventory/setStatus': handleInventorySetStatus,
  'salesChannelSetup/inventory/submit/start': handleInventorySubmitStart,
  'salesChannelSetup/inventory/submit/success': handleInventorySubmitSuccess,
  'salesChannelSetup/inventory/submit/error': handleInventorySubmitError,
  'salesChannelSetup/billing/selectEntity': handleBillingSelectEntity,
  'salesChannelSetup/billing/setMode': handleBillingSetMode,
  'salesChannelSetup/billing/updateField': handleBillingUpdateField,
  'salesChannelSetup/billing/setFormErrors': handleBillingSetFormErrors,
  'salesChannelSetup/billing/setTrigger': handleBillingSetTrigger,
  'salesChannelSetup/billing/setStatus': handleBillingSetStatus,
  'salesChannelSetup/billing/submit/start': handleBillingSubmitStart,
  'salesChannelSetup/billing/submit/success': handleBillingSubmitSuccess,
  'salesChannelSetup/billing/submit/error': handleBillingSubmitError,
  'salesChannelSetup/billing/create/start': handleBillingCreateStart,
  'salesChannelSetup/billing/create/success': handleBillingCreateSuccess,
  'salesChannelSetup/billing/create/error': handleBillingCreateError,
  'salesChannelSetup/invoicing/selectConnector': handleInvoicingSelectConnector,
  'salesChannelSetup/invoicing/updateCredentials': handleInvoicingUpdateCredentials,
  'salesChannelSetup/invoicing/provision/start': handleInvoicingProvisionStart,
  'salesChannelSetup/invoicing/provision/success': handleInvoicingProvisionSuccess,
  'salesChannelSetup/invoicing/provision/error': handleInvoicingProvisionError,
  'salesChannelSetup/logistics/selectIntegration': handleLogisticsSelectIntegration,
  'salesChannelSetup/logistics/setTrigger': handleLogisticsSetTrigger,
  'salesChannelSetup/logistics/setStatus': handleLogisticsSetStatus,
  'salesChannelSetup/logistics/submit/start': handleLogisticsSubmitStart,
  'salesChannelSetup/logistics/submit/success': handleLogisticsSubmitSuccess,
  'salesChannelSetup/logistics/submit/error': handleLogisticsSubmitError,
  'salesChannelSetup/logistics/setMode': handleLogisticsSetMode,
  'salesChannelSetup/logistics/create/selectConnector': handleLogisticsCreateSelectConnector,
  'salesChannelSetup/logistics/create/updateCredentials': handleLogisticsCreateUpdateCredentials,
  'salesChannelSetup/logistics/create/setInstanceName': handleLogisticsCreateSetInstanceName,
  'salesChannelSetup/logistics/register/start': handleLogisticsRegisterStart,
  'salesChannelSetup/logistics/register/success': handleLogisticsRegisterSuccess,
  'salesChannelSetup/logistics/register/error': handleLogisticsRegisterError,
  'salesChannelSetup/complete/start': handleCompleteStart,
  'salesChannelSetup/complete/success': handleCompleteSuccess,
  'salesChannelSetup/complete/error': handleCompleteError
};
